import React, { useEffect, useRef, useState } from "react";
import Chart from "react-apexcharts";
import { format } from "date-fns";
import Styles from "./HistoryPriceGraph.module.css";
import { getHistoricalGoldPriceAction } from "../../../store/actions/dashboard/DashboardActions";
import { useDispatch, useSelector } from "react-redux";
import ShimmerLoader from "../../pages/ShimmerLoader/ShimmerLoader";
import currenyFormat from "../common/currentFormat";

function HistoryPriceGraph() {
  const [selectedCategory, setSelectedCategory] = useState("1M");
  const chartContainerRef = useRef(null);
  const dispatch = useDispatch();
  const [historicalGoldPrice, setHistoricalGoldPrice] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setSelectedCategory(localStorage.getItem("historicalPriceTimeDuration") || "1M");
    const payload = {
      timeFrame: selectedCategory,
    };
    const fetchHistoricalGoldPrice = async () => {
      try {
        setLoading(true);
        const response = await getHistoricalGoldPriceAction(payload);
        setHistoricalGoldPrice(response.result.data);
        setLoading(false);
      } catch (error) {
        setLoading(false);
        console.error("Failed to fetchSalesHistory:", error);
      }
    };
    fetchHistoricalGoldPrice();
  }, [selectedCategory]);

  const parseAndFormatDate = (dateStr) => {
    const year = dateStr.slice(0, 4);
    const month = dateStr.slice(4, 6) - 1;
    const day = dateStr.slice(6, 8);

    const date = new Date(year, month, day);
    return format(date, "dd MMM yyyy");
  };

  const formatChartData = (data) => {
    if (!Array.isArray(data) || data.length === 0) {
      return { categories: [], seriesData: [] };
    }
    const categories = data.map((item) => parseAndFormatDate(item.date_time));
    const seriesData = data.map((item) => item.buy_pretax);
    return {
      categories,
      seriesData,
    };
  };

  const { categories, seriesData } = formatChartData(historicalGoldPrice || []);

  const data = {
    series: [
      {
        name: "",
        data: seriesData,
      },
    ],
    options: {
      chart: {
        height: 450,
        width: 300,
        type: "area",
        events: {
          mounted: function (chartContext, config) {
            setTimeout(() => {
              if (chartContainerRef.current) {
                const xAxisContainer = chartContainerRef.current.querySelector(".apexcharts-xaxis");
                if (xAxisContainer) {
                  xAxisContainer.classList.add(Styles.xAxisWrapper);
                }
              }
            }, 100);
          },
        },
        zoom: {
          enabled: false,
        },
        toolbar: {
          show: false,
        },
      },
      dataLabels: {
        enabled: false,
      },
      title: {
        text: "Historical Price",
        style: {
          fontSize: "18px",
          fontWeight: "600",
          color: "#191630",
          lineHeight: "27px",
          fontFamily: "poppins",
        },
      },
      legend: {
        show: false,
      },
      grid: {
        show: false,
      },
      stroke: {
        curve: "smooth",
        width: 3,
        colors: ["#E0A33E"],
      },
      fill: {
        type: "gradient",
        gradient: {
          shade: "light",
          type: "vertical",
          shadeIntensity: 0.5,
          gradientToColors: ["#E0A33E"],
          inverseColors: false,
          opacityFrom: 0.7,
          opacityTo: 0.2,
          stops: [0, 100],
        },
        colors: ["#E0A33E"],
      },
      xaxis: {
        categories: categories,
        labels: {
          show: false,
          style: {
            fontSize: "13px",
            fontFamily: "poppins",
            colors: "#191630",
          },
        },
        axisBorder: {
          show: false, // Hide axis border
        },
        axisTicks: {
          show: false, // Hide axis ticks
        },
        lines: {
          show: false, // Hide lines
        },
      },
      yaxis: {
        labels: {
          show: true,
          style: {
            fontSize: "13px",
            fontFamily: "poppins",
            colors: "#191630",
          },
        },
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
        lines: {
          show: false,
        },
      },
      tooltip: {
        custom: function ({ series, seriesIndex, dataPointIndex }) {
          const date = categories[dataPointIndex];
          const value = series[seriesIndex][dataPointIndex];
          // return `<div style="padding: 10px;">
          //           <div>Price: ₹ ${value}</div>
          //           <div>Date: ${date}</div>
          //         </div>`;
          return `<div style="padding: 10px;">
                    <div>Price: ${currenyFormat(value)}</div>
                    <div>Date: ${date}</div>
                  </div>`;
        },
        shared: false,
      },
    },
  };

  const handleLabelClick = (value) => {
    localStorage.setItem("historicalPriceTimeDuration", value);
    setSelectedCategory(value);
  };

  useEffect(() => {
    if (chartContainerRef.current) {
      const xAxisContainer = chartContainerRef.current.querySelector(".apexcharts-xaxis");
      if (xAxisContainer) {
        xAxisContainer.classList.add(Styles.xAxisWrapper);
      }
    }
  }, []);
  if (loading) {
    return <div className={Styles.loaderContainer}>
      <ShimmerLoader width='95%' height='20%' count={2} />;
    </div>
  }
  if (!loading) {
    return (
      <div className={`${Styles.chartContainer}`} ref={chartContainerRef} style={{ marginBottom: "20px", marginTop: "20px", marginLeft: "20px", marginRight: "20px" }}>
        <Chart options={data.options} series={data.series} type='area' height={280} />
        <hr />
        <div className='d-flex align-items-center justify-content-evenly'>
          <button className={`btn btn-outline-secondary ${Styles.customButton}  ${selectedCategory === "1W" ? Styles.activeButton : ""}`} onClick={() => handleLabelClick("1W")}>
            1W
          </button>
          <button className={`btn btn-outline-secondary ${Styles.customButton} ${selectedCategory === "1M" ? Styles.activeButton : ""}`} onClick={() => handleLabelClick("1M")}>
            1M
          </button>
          <button className={`btn btn-outline-secondary ${Styles.customButton} ${selectedCategory === "1Y" ? Styles.activeButton : ""}`} onClick={() => handleLabelClick("1Y")}>
            1Y
          </button>
          <button className={`btn btn-outline-secondary ${Styles.customButton} ${selectedCategory === "3Y" ? Styles.activeButton : ""}`} onClick={() => handleLabelClick("3Y")}>
            3Y
          </button>
          <button className={`btn btn-outline-secondary ${Styles.customButton} ${selectedCategory === "5Y" ? Styles.activeButton : ""}`} onClick={() => handleLabelClick("5Y")}>
            5Y
          </button>
        </div>
      </div>
    );
  }
}

export default HistoryPriceGraph;
